import {observer} from 'mobx-react-lite'
import React from 'react'
import {Lang} from 'yougile-lang'
import {HTMLUtil, Util} from 'yougile-util'
import {getChecklistsAsText, GroupChat, Hub, User} from 'yougile-model'
import {DirectMessages} from '@client/direct-messages'
import {EventBus} from '@client/event-bus'
import {openChatEvent} from '@client/events'
import {Sync} from '@client/sync'
import {Debouncer} from '@common/debouncer'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {Loader} from '@components/common/base/loader'
import {useIdTaskUpdater} from '@components/id-tasks/use-id-task-updater'
import {useLoadedLazyTaskEffect} from '@containers/hooks'
import {TaskChatTab} from '@extensions/app-persistence'
import {ChatMessageUtils} from '@extensions/chat-message-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {MessageUtils} from '@extensions/message-utils'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {TaskChatProxyService} from '@extensions/proxies/task-chat-proxy'
import {
  PackedId,
  SearchResultItem,
  SearchResultItemType,
  unpackId
} from '@extensions/search'
import {ChatList} from '../chat-list/chat-list'
import {PersonalChatListItem} from '../personal-chat-list/personal-chat-list-item'
import {GroupChatListItem} from '../task-chat-list/group-chat-list-item'
import {TaskChatListItem} from '../task-chat-list/task-chat-list-item'

const NotFound = () => {
  const bem = Util.bemFrom('search', 'results', 'not-found')
  return <div className={bem()}>{Lang.msg.notFound}</div>
}

interface SearchResultsProps {
  readonly search: string
  onItemClick?: (chatId: string) => void
}

const typeToTab: Partial<Record<SearchResultItemType, TaskChatTab>> = {
  description: 'description',
  'checklist-title': 'checklists',
  'checklist-item': 'checklists'
}

const itemClick = (chatId: PackedId) => {
  const {id, messageTime, type} = unpackId(chatId)
  const box = Sync.getCurrentSandbox()
  const obj = box.get(id)
  if (obj instanceof User) {
    const hub = DirectMessages.requestChatWith(id)
    EventBus.dispatch(openChatEvent(hub.id, messageTime, undefined, false))
  } else {
    EventBus.dispatch(openChatEvent(id, messageTime, typeToTab[type], false))
  }
}

function ensureMessage<T>(msg: T | undefined): asserts msg is T {
  if (!msg) {
    throw new Error('No message')
  }
}

export const SearchResultView = (item: SearchResultItem) => {
  const {entity, type, messageTime, searchQuery, scoreTime} = item
  const box = Sync.get({companyId: entity.companyId})
  const withCurrentUser =
    entity instanceof Hub && ChatUtils.isMyChat(Sync.get(entity), entity.id)
  useLoadedLazyTaskEffect(
    entity instanceof Hub ? entity : undefined,
    'search-result-view'
  )
  if (entity.isLazy) {
    return <Loader size="xsmall" />
  }
  if (entity instanceof User) {
    const props = {
      ...PersonalChatProxy.createForCurrentUser(entity),
      lastMessageTimestamp: item.scoreTime,
      highlightType: type
    }
    if (messageTime) {
      const hub = DirectMessages.requestChatWith(entity.id)
      const msg = item.messageData ?? hub.chat.messages.get(messageTime)
      ensureMessage(msg)
      const msgProps = {
        ...props,
        highlightText: searchQuery,
        lastMessageText: MessageUtils.renderHtmlForSearch(box, msg),
        startFrom: item.start,
        endAt: item.end,
        lastMessageTimestamp: msg.timestamp
      }
      return <PersonalChatListItem {...msgProps} locationType="search" />
    }
    return (
      <PersonalChatListItem
        {...props}
        highlightText={searchQuery}
        locationType="search"
      />
    )
  } else if (entity instanceof Hub) {
    if (GroupChat.isGroupChat(entity)) {
      const box = Sync.get(entity)
      const props = GroupChatProxy.create(box, entity)
      if (type === 'description') {
        const lastMessageText = HTMLUtil.extractText(entity.description)
        const descrProps = {
          ...props,
          lastMessageText,
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: scoreTime,
          lastMessageAuthor: Lang.msg.description,
          highlightText: searchQuery,
          highlightType: type
        }
        return <GroupChatListItem {...descrProps} locationType="search" />
      } else if (type === 'checklist-title' || type === 'checklist-item') {
        const checklistsAsText = getChecklistsAsText(
          entity.data.checklists ?? []
        )
        const subtasksProps = {
          ...props,
          lastMessageText: checklistsAsText,
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: scoreTime,
          lastMessageAuthor: Lang.msg.subtasks,
          highlightText: searchQuery,
          highlightType: type
        }
        return <GroupChatListItem {...subtasksProps} locationType="search" />
      } else if (messageTime) {
        const msg = item.messageData ?? entity.chat.messages.get(messageTime)
        ensureMessage(msg)
        const author = ChatMessageUtils.authorRealNameForDisplay(msg, entity)
        const messageProps = {
          ...props,
          lastMessageText: MessageUtils.renderHtmlForSearch(box, msg),
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: msg.timestamp,
          lastMessageAuthor: author,
          highlightText: searchQuery,
          highlightType: type
        }
        return <GroupChatListItem {...messageProps} locationType="search" />
      } else {
        return (
          <GroupChatListItem
            {...props}
            highlightText={searchQuery}
            locationType="search"
            highlightType={type}
            lastMessageTimestamp={scoreTime}
          />
        )
      }
    } else {
      const props = TaskChatProxyService.createForCurrentUser(entity)
      if (type === 'description') {
        const lastMessageText = HTMLUtil.extractText(entity.description)
        const descrProps = {
          ...props,
          lastMessageText,
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: scoreTime,
          lastMessageAuthor: Lang.msg.description,
          highlightText: searchQuery,
          highlightType: type
        }
        return (
          <TaskChatListItem
            {...descrProps}
            withMe={withCurrentUser}
            locationType="search"
          />
        )
      } else if (type === 'checklist-title' || type === 'checklist-item') {
        const checklistsAsText = getChecklistsAsText(
          entity.data.checklists ?? []
        )
        const subtasksProps = {
          ...props,
          lastMessageText: checklistsAsText,
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: scoreTime,
          lastMessageAuthor: Lang.msg.subtasks,
          highlightText: searchQuery,
          highlightType: type
        }
        return (
          <TaskChatListItem
            {...subtasksProps}
            withMe={withCurrentUser}
            locationType="search"
          />
        )
      } else if (messageTime) {
        const msg = item.messageData ?? entity.chat.messages.get(messageTime)
        ensureMessage(msg)
        const author = ChatMessageUtils.authorRealNameForDisplay(msg, entity)
        const messageProps = {
          ...props,
          lastMessageText: MessageUtils.renderHtmlForSearch(box, msg),
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: msg.timestamp,
          lastMessageAuthor: author,
          highlightText: searchQuery,
          highlightType: type
        }
        return (
          <TaskChatListItem
            {...messageProps}
            withMe={withCurrentUser}
            locationType="search"
          />
        )
      } else if (type === 'id') {
        const messageProps = {
          ...props,
          lastMessageText: idTasksApi.getIdTask(entity.id) || '',
          startFrom: item.start,
          endAt: item.end,
          lastMessageTimestamp: scoreTime,
          highlightText: searchQuery,
          highlightType: type
        }
        return (
          <TaskChatListItem
            {...messageProps}
            withMe={withCurrentUser}
            locationType="search"
          />
        )
      } else {
        return (
          <TaskChatListItem
            {...props}
            withMe={withCurrentUser}
            highlightText={searchQuery}
            locationType="search"
            highlightType={type}
            lastMessageTimestamp={scoreTime}
          />
        )
      }
    }
  }
  throw new Error('Unexpected entity type')
}

const trackingDebouncer = new Debouncer(10000)

export const SearchResults = observer(
  ({search, onItemClick}: SearchResultsProps) => {
    useIdTaskUpdater()

    const bem = Util.bemFrom('search', 'results')
    const ix = Sync.searchIndices.get(globals.getCompanyId())
    if (!ix) {
      return (
        <div className={bem()}>
          <NotFound />
        </div>
      )
    }

    const results = ix.search(search, 2000)
    if (results.length) {
      trackingDebouncer.runNowButNotRightAfter(() =>
        Tracking.send(TrackingEvent.projectSearch)
      )
    }

    return (
      <div className={bem()}>
        {results.length > 0 && (
          <ChatList
            chats={results}
            chatView={SearchResultView}
            itemClick={onItemClick ?? itemClick}
          />
        )}
        {results.length === 0 && <NotFound />}
      </div>
    )
  }
)
